import { DeleteAssetProps, DeleteType } from './types';
import {
  getModalContentProps,
  HOMEPAGE_LINKED_ASSETS,
  useCheckIfResourceInUseByHomePage,
  useCheckIfReportInUse,
  useCheckIfWsInUse,
} from './utils';
import {
  AlertDialog,
  ConfirmDeleteDialog,
  ConfirmDeleteWithYesInputDialog,
  ModalLoader,
  ModalSize,
  ModalTemplateProps,
} from '@ardoq/modal';
import { IconName } from '@ardoq/icons';
import { noop } from 'lodash';
import { WorkspaceInUseDialog } from './WorkspaceInUseDialog/WorkspaceInUseDialog';
import { AssetInUseByHomePageDialog } from './AssetInUseByHomePageDialog/AssetInUseByHomePageDialog';
import { t, type I18nLocale } from '@ardoq/i18n';
import { type Locale } from '@ardoq/locale';
import { type APISlideAttributes } from '@ardoq/api-types';
import { connect } from '@ardoq/rxbeach';
import { combineLatest, map } from 'rxjs';
import { presentations$ } from 'streams/presentations/presentations$';
import { type PresentationsStreamShape } from 'streams/presentations/types';
import { slides$ } from 'streams/slides/slides$';

type DeleteAssetDialogProps = {
  onConfirm?: () => any;
  onCancel?: () => any;
  locale: Locale;
  i18nLocale: I18nLocale;
  presentationsState: PresentationsStreamShape;
  slides: APISlideAttributes[];
} & DeleteAssetProps &
  Pick<ModalTemplateProps, 'inputDataTestId'>;

const DeleteAssetDialogBase = ({
  onConfirm = noop,
  onCancel = noop,
  inputDataTestId,
  locale,
  i18nLocale,
  presentationsState,
  slides,
  ...deleteAssetProps
}: DeleteAssetDialogProps) => {
  const {
    loading: wsUsageIsLoading,
    canShowDeleteDialog,
    workspaceNamesInUse,
    scenariosUsingSelectedWs,
  } = useCheckIfWsInUse(deleteAssetProps);

  const {
    loading: reportUsageIsLoading,
    dashboardsUsingSelectedReports,
    totalDashboardsUsingSelectedReports,
  } = useCheckIfReportInUse(deleteAssetProps);

  const { loading: assetInHomePageUsageIsLoading, assetUsedInHomePageName } =
    useCheckIfResourceInUseByHomePage(deleteAssetProps);

  const {
    title,
    subtitle,
    listOptions,
    warningBlockMessage,
    confirmButtonTitle,
    text,
    children,
  } = getModalContentProps({
    assetProps: deleteAssetProps,
    locale,
    i18nLocale,
    presentationsState,
    slides,
  });

  if (wsUsageIsLoading || reportUsageIsLoading || assetInHomePageUsageIsLoading)
    return <ModalLoader />;

  if (
    !canShowDeleteDialog &&
    deleteAssetProps.deleteType === DeleteType.MULTIPLE
  ) {
    return (
      <AlertDialog
        title={title || ''}
        subtitle={t(
          i18nLocale,
          'assetsBrowser:modals.delete.alert.cantDelete',
          "Can't delete assets"
        )}
        text={t(
          i18nLocale,
          'assetsBrowser:modals.delete.alert.componentsInUse',
          'Components from the following workspaces are in use in one or more scenarios:'
        )}
        listOptions={workspaceNamesInUse.map(name => ({
          iconName: IconName.WORKSPACE,
          strongText: name,
        }))}
        text2={t(
          i18nLocale,
          'assetsBrowser:modals.delete.alert.howToDelete',
          'To delete these assets, open the scenarios and exclude all components from these workspaces.'
        )}
        onClose={onCancel}
      />
    );
  }

  if (
    !canShowDeleteDialog &&
    deleteAssetProps.deleteType === DeleteType.WORKSPACE
  ) {
    return (
      <WorkspaceInUseDialog
        workspaceName={deleteAssetProps.name}
        scenariosUsingTheWorkspace={scenariosUsingSelectedWs}
        closeDialog={onCancel}
      />
    );
  }
  const commonDeleteProps = {
    title: title || '',
    onConfirm,
    onCancel,
    subtitle,
    listOptions,
    confirmButtonClickId: 'confirm-delete',
    inputDataTestId,
    warningBlockMessage,
    confirmButtonTitle,
    cancelButtonTitle: t(
      i18nLocale,
      'assetsBrowser:modals.delete.cancelButton',
      'Cancel'
    ),
    yesInputLabel: t(
      i18nLocale,
      'assetsBrowser:modals.delete.yesInputLabel',
      'Type "yes" to continue'
    ),
    text,
    body: children,
  };
  if (
    deleteAssetProps.deleteType === DeleteType.REPORT &&
    dashboardsUsingSelectedReports.length
  ) {
    const numberOfAffectedDashboardsTheUserCantAccess =
      totalDashboardsUsingSelectedReports -
      dashboardsUsingSelectedReports.length;

    return (
      <ConfirmDeleteWithYesInputDialog
        {...commonDeleteProps}
        text={
          dashboardsUsingSelectedReports.length === 1
            ? t(
                i18nLocale,
                'assetsBrowser:modals.delete.report.usedInSingular',
                'This report is used in 1 dashboard:'
              )
            : t(
                i18nLocale,
                'assetsBrowser:modals.delete.report.usedInPlural',
                { count: dashboardsUsingSelectedReports.length },
                'This report is used in {{count}} dashboards:'
              )
        }
        listOptions={dashboardsUsingSelectedReports.map(({ name }) => ({
          iconName: IconName.DASHBOARD,
          strongText: name,
        }))}
        warningBlockMessage={
          numberOfAffectedDashboardsTheUserCantAccess
            ? numberOfAffectedDashboardsTheUserCantAccess === 1
              ? t(
                  i18nLocale,
                  'assetsBrowser:modals.delete.report.noAccessSingular',
                  "...and in 1 dashboard you don't have access to."
                )
              : t(
                  i18nLocale,
                  'assetsBrowser:modals.delete.report.noAccessPlural',
                  { count: numberOfAffectedDashboardsTheUserCantAccess },
                  "...and in {{count}} dashboards you don't have access to."
                )
            : undefined
        }
      />
    );
  }
  if (
    HOMEPAGE_LINKED_ASSETS.includes(deleteAssetProps.deleteType) &&
    assetUsedInHomePageName
  ) {
    return (
      <AssetInUseByHomePageDialog
        {...commonDeleteProps}
        deleteType={deleteAssetProps.deleteType}
        assetUsedInHomePageName={assetUsedInHomePageName}
        i18nLocale={i18nLocale}
      />
    );
  }
  if (deleteAssetProps.deleteType === DeleteType.REFERENCE_TYPE) {
    return (
      <ConfirmDeleteDialog
        {...commonDeleteProps}
        modalSize={text || children ? ModalSize.M : ModalSize.S}
      />
    );
  }
  return <ConfirmDeleteWithYesInputDialog {...commonDeleteProps} />;
};

const DeleteAssetDialog = connect(
  DeleteAssetDialogBase,
  combineLatest({ presentationsState: presentations$, slides: slides$ }).pipe(
    map(({ presentationsState, slides }) => ({
      presentationsState,
      slides: slides.list,
    }))
  )
);

export { DeleteAssetDialog };
